import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Landmark, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function BankReconciliation() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", bank_name: "", account_number: "", period_from: "", period_to: "", opening_balance: 0, closing_balance: 0, lines: [] });

  const load = async () => { try { const { data } = await api.get("/bank-recon"); setItems(data); } catch (e) { toast.error(formatErr(e)); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: `تسوية ${new Date().toLocaleDateString("ar-EG")}`, bank_name: "", account_number: "", period_from: "", period_to: "", opening_balance: 0, closing_balance: 0, lines: [] }); setOpen(true); };
  const addLine = () => setForm(f => ({...f, lines: [...f.lines, { date: new Date().toISOString().slice(0,10), description: "", debit: 0, credit: 0, reference: "", matched: false }]}));
  const updateLine = (i, patch) => setForm(f => ({...f, lines: f.lines.map((l, idx) => idx === i ? {...l, ...patch} : l)}));
  const rmLine = (i) => setForm(f => ({...f, lines: f.lines.filter((_, idx) => idx !== i)}));

  const save = async () => {
    if (!form.name.trim() || !form.period_from || !form.period_to) return toast.error("املأ الحقول الأساسية");
    try { await api.post("/bank-recon", form); toast.success("تم حفظ التسوية"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!confirm("حذف؟")) return; try { await api.delete(`/bank-recon/${id}`); load(); } catch(e){ toast.error(formatErr(e)); } };

  const totalDebit = form.lines.reduce((s, l) => s + (+l.debit || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (+l.credit || 0), 0);
  const computedClose = form.opening_balance + totalCredit - totalDebit;
  const diff = form.closing_balance - computedClose;

  return (
    <div className="space-y-4" data-testid="bank-recon-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Landmark className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">التسوية البنكية</h1>
          <p className="text-sm text-muted-foreground">قارن كشف البنك مع سجلاتك واكشف الفروقات</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-recon-btn"><Plus className="w-4 h-4" /> تسوية جديدة</Button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>الاسم</TableHead><TableHead>البنك</TableHead>
            <TableHead>الفترة</TableHead><TableHead>الرصيد الافتتاحي</TableHead>
            <TableHead>الرصيد الختامي</TableHead><TableHead>الفرق</TableHead>
            <TableHead>الحالة</TableHead><TableHead>إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(r => (
              <TableRow key={r.id} data-testid={`recon-row-${r.id}`}>
                <TableCell className="font-bold">{r.name}</TableCell>
                <TableCell>{r.bank_name || "—"}</TableCell>
                <TableCell className="text-xs">{r.period_from} → {r.period_to}</TableCell>
                <TableCell className="font-mono">{r.opening_balance.toLocaleString("ar-EG")}</TableCell>
                <TableCell className="font-mono">{r.closing_balance.toLocaleString("ar-EG")}</TableCell>
                <TableCell className={`font-mono ${Math.abs(r.difference) < 1 ? "text-emerald-700" : "text-red-700 font-bold"}`}>{r.difference?.toFixed(2)}</TableCell>
                <TableCell>{r.status === "matched" ? <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> متطابق</span> : <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> فارق</span>}</TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={()=>del(r.id)} className="h-7 text-destructive" data-testid={`del-recon-${r.id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">لا توجد عمليات تسوية</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="recon-dialog">
          <DialogHeader><DialogTitle>تسوية بنكية جديدة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><Label>الاسم</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="recon-name-input" /></div>
              <div><Label>البنك</Label><Input value={form.bank_name} onChange={(e)=>setForm({...form, bank_name:e.target.value})} data-testid="recon-bank-input" /></div>
              <div><Label>رقم الحساب</Label><Input value={form.account_number} onChange={(e)=>setForm({...form, account_number:e.target.value})} dir="ltr" className="font-mono" data-testid="recon-acc-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>من تاريخ</Label><Input type="date" value={form.period_from} onChange={(e)=>setForm({...form, period_from:e.target.value})} data-testid="recon-from-input" /></div>
              <div><Label>إلى تاريخ</Label><Input type="date" value={form.period_to} onChange={(e)=>setForm({...form, period_to:e.target.value})} data-testid="recon-to-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>الرصيد الافتتاحي (كشف البنك)</Label><Input type="number" step="0.01" value={form.opening_balance} onChange={(e)=>setForm({...form, opening_balance:+e.target.value})} data-testid="recon-open-input" /></div>
              <div><Label>الرصيد الختامي (كشف البنك)</Label><Input type="number" step="0.01" value={form.closing_balance} onChange={(e)=>setForm({...form, closing_balance:+e.target.value})} data-testid="recon-close-input" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>الحركات من كشف البنك</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} className="gap-1 h-7" data-testid="add-recon-line"><Plus className="w-3 h-3" /> سطر</Button>
              </div>
              <div className="border border-border rounded-md overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-32">التاريخ</TableHead><TableHead>الوصف</TableHead>
                    <TableHead className="w-24">مدين (خروج)</TableHead><TableHead className="w-24">دائن (دخول)</TableHead>
                    <TableHead className="w-24">مرجع</TableHead><TableHead className="w-8"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {form.lines.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell><Input type="date" value={l.date} onChange={(e)=>updateLine(i, {date:e.target.value})} className="h-8" /></TableCell>
                        <TableCell><Input value={l.description} onChange={(e)=>updateLine(i, {description:e.target.value})} className="h-8" /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={l.debit} onChange={(e)=>updateLine(i, {debit:+e.target.value})} className="h-8" data-testid={`recon-debit-${i}`} /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={l.credit} onChange={(e)=>updateLine(i, {credit:+e.target.value})} className="h-8" data-testid={`recon-credit-${i}`} /></TableCell>
                        <TableCell><Input value={l.reference} onChange={(e)=>updateLine(i, {reference:e.target.value})} className="h-8 font-mono" dir="ltr" /></TableCell>
                        <TableCell><button onClick={()=>rmLine(i)} className="text-destructive"><Trash2 className="w-3 h-3" /></button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <Card className="p-3 bg-muted/50">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>إجمالي مدين: <span className="font-mono font-bold">{totalDebit.toFixed(2)}</span></div>
                <div>إجمالي دائن: <span className="font-mono font-bold">{totalCredit.toFixed(2)}</span></div>
                <div>الرصيد المحسوب: <span className="font-mono font-bold">{computedClose.toFixed(2)}</span></div>
                <div>الفرق: <span className={`font-mono font-bold ${Math.abs(diff) < 1 ? "text-emerald-700" : "text-red-700"}`}>{diff.toFixed(2)}</span></div>
              </div>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-recon-btn">حفظ التسوية</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
